import QS from 'query-string';
import { getApiDomain } from '~/utils/get-api-domain';
import { authStorage } from '~/services/auth/auth-storage';
export class ApiFetcher {
  private baseUrl: string;
  private getToken?: () => Nullable<string>;

  constructor(baseUrl: string, getToken?: () => Nullable<string>) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    try {
      if (!response.ok) {
        const errorResponse = await response.json();
        throw errorResponse;
      }
      const contentType = response.headers.get('Content-Type');

      if (contentType && !contentType.includes('application/json')) {
        return response.text() as unknown as T;
      }

      const data: T = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  private async fetcher(
    endpoint: string | URL | Request,
    requestInit?: RequestInit,
    requestBody?: object,
  ): Promise<Response> {
    const defaultHeaders = {
      'Content-Type': 'application/json',
    } as Record<string, string>;

    // fetcher 서버에서 호출 시 x-forwarded-for 포함
    if (typeof globalThis.window === 'undefined') {
      //
      try {
        const { headers: nextHeaders } = await import('next/headers');
        const headersList = await nextHeaders();
        const forwardedFor = headersList.get('x-forwarded-for') || '';
        if (forwardedFor) {
          defaultHeaders['x-forwarded-for'] = forwardedFor;
        }
      } catch (e) {
        console.error('Headers import error:', e);
      }
    }

    // 토큰은 로그인/로그아웃으로 바뀌므로 인스턴스에 보관하지 않고 매 요청마다 읽는다.
    const token = this.getToken?.();

    if (token) {
      defaultHeaders['X-BUN-AUTH-TOKEN'] = token;
    }
    

    return fetch(this.baseUrl + endpoint, {
      cache: 'no-cache',
      credentials: 'include',
      ...requestInit,
      // requestInit.headers 가 defaultHeaders 를 통째로 덮어쓰지 않도록 병합한다.
      headers: { ...defaultHeaders, ...requestInit?.headers },
      ...(requestBody && { body: JSON.stringify(requestBody) }),
    });
  }

  public async get<T>(endPoint: string, queryParams?: object, requestInit?: RequestInit): Promise<T> {
    try {
      const params = queryParams ? QS.stringify(queryParams) : '';
      const response = await this.fetcher(endPoint + `?${params}`, {
        method: 'GET',
        ...requestInit,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      throw error;
    }
  }
  public async post<T>(endpoint: string, body?: object, requestInit?: RequestInit): Promise<T> {
    try {
      const response = await this.fetcher(
        endpoint,
        {
          method: 'POST',
          ...requestInit,
        },
        body,
      );

      return this.handleResponse<T>(response);
    } catch (error) {
      throw error;
    }
  }
  public async put<T>(endpoint: string, body?: object, requestInit?: RequestInit): Promise<T> {
    try {
      const response = await this.fetcher(
        endpoint,
        {
          method: 'PUT',
          ...requestInit,
        },
        body,
      );

      return this.handleResponse<T>(response);
    } catch (error) {
      throw error;
    }
  }
  public async delete<T>(endpoint: string, requestInit?: RequestInit): Promise<T> {
    try {
      const response = await this.fetcher(endpoint, {
        method: 'DELETE',
        ...requestInit,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      throw error;
    }
  }
}
export const apiFetcher = new ApiFetcher(getApiDomain() as string, () => authStorage.getToken());
